import React, { useEffect, useState } from "react";
import { Save, Lock, User as UserIcon, Check } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

/** /profile/edit — every user (viewer/creator/admin) can edit name, @handle, bio, picture, + password. */
export default function ProfileEdit() {
  const { user, refresh } = useAuth();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", handle: "", bio: "", picture: "" });
  const [pw, setPw] = useState({ old_password: "", new_password: "", confirm: "" });
  const [pwBusy, setPwBusy] = useState(false);
  const [uploadBusy, setUploadBusy] = useState(false);

  useEffect(() => {
    if (!user) return;
    setForm({
      name: user.name || "",
      handle: user.handle || "",
      bio: user.bio || "",
      picture: user.picture || "",
    });
  }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/auth/profile", {
        name: form.name,
        handle: form.handle.replace(/^@/, "").toLowerCase(),
        bio: form.bio,
        picture: form.picture,
      });
      await refresh();
      toast.success("Profile updated");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const submitPw = async (e) => {
    e.preventDefault();
    if (pw.new_password !== pw.confirm) return toast.error("New passwords don't match");
    if (pw.new_password.length < 6) return toast.error("At least 6 characters please");
    setPwBusy(true);
    try {
      await api.put("/auth/password", { old_password: pw.old_password, new_password: pw.new_password });
      toast.success("Password changed");
      setPw({ old_password: "", new_password: "", confirm: "" });
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setPwBusy(false); }
  };

  const onPic = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, picture: `/api/files/${data.storage_path}` }));
      toast.success("Avatar uploaded");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploadBusy(false); }
  };

  if (!user) return <div className="pt-32 text-center text-white/60">Sign in required.</div>;

  return (
    <div data-testid="profile-edit-page" className="pt-32 pb-24 px-4 sm:px-8 md:px-12 max-w-3xl mx-auto">
      <div>
        <div className="text-xs uppercase tracking-[0.3em] text-tv-accent font-bold">Profile</div>
        <h1 className="font-display font-black text-5xl sm:text-6xl text-white mt-3 tracking-tighter">Edit your profile</h1>
        <p className="text-white/60 mt-2">Your name, @handle, bio, and avatar appear across Tap In TV.</p>
      </div>

      <form onSubmit={submit} className="tv-glass rounded-2xl border border-white/10 p-6 mt-8 space-y-5">
        <div className="flex items-center gap-5">
          <img
            src={form.picture || `https://api.dicebear.com/9.x/initials/svg?seed=${encodeURIComponent(form.name || "U")}`}
            alt=""
            className="w-24 h-24 rounded-full object-cover border-2 border-white/15"
          />
          <label className="cursor-pointer text-sm bg-white/10 hover:bg-white/20 text-white font-bold px-4 py-2 rounded-full">
            <input type="file" accept="image/*" className="hidden" onChange={onPic} data-testid="profile-avatar-input" disabled={uploadBusy} />
            {uploadBusy ? "Uploading…" : "Change avatar"}
          </label>
        </div>

        <label className="block">
          <span className="text-[10px] uppercase tracking-wider text-white/50 font-bold">Display name</span>
          <input data-testid="profile-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} maxLength={80} required className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-tv-accent" />
        </label>

        <label className="block">
          <span className="text-[10px] uppercase tracking-wider text-white/50 font-bold">@handle</span>
          <div className="mt-1 flex items-center bg-white/5 border border-white/10 rounded-lg focus-within:border-tv-accent">
            <span className="pl-3 text-white/50 text-sm">@</span>
            <input data-testid="profile-handle-input" value={form.handle} onChange={(e) => setForm({ ...form, handle: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "").slice(0, 24) })} className="flex-1 px-2 py-2 bg-transparent outline-none text-white text-sm" placeholder="your_name" />
          </div>
          <div className="text-[11px] text-white/40 mt-1">Lowercase letters, numbers, underscores. 3–24 chars.</div>
        </label>

        <label className="block">
          <span className="text-[10px] uppercase tracking-wider text-white/50 font-bold">Bio</span>
          <textarea data-testid="profile-bio-input" value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} rows={3} maxLength={600} className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-tv-accent resize-none" />
          <div className="text-[11px] text-white/40 mt-1">{form.bio.length}/600</div>
        </label>

        <div className="flex justify-end">
          <button type="submit" disabled={saving} data-testid="profile-save-btn" className="bg-tv-accent hover:bg-yellow-400 text-black font-bold px-6 py-2.5 rounded-full flex items-center gap-2 disabled:opacity-60">
            {saving ? "Saving…" : <><Save className="w-4 h-4" /> Save profile</>}
          </button>
        </div>
      </form>

      <form onSubmit={submitPw} className="tv-glass rounded-2xl border border-white/10 p-6 mt-6 space-y-4">
        <div className="flex items-center gap-2 text-white font-display font-bold text-xl">
          <Lock className="w-5 h-5 text-tv-accent" /> Change password
        </div>
        <label className="block">
          <span className="text-[10px] uppercase tracking-wider text-white/50 font-bold">Current password</span>
          <input data-testid="password-old-input" type="password" value={pw.old_password} onChange={(e) => setPw({ ...pw, old_password: e.target.value })} className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-tv-accent" />
        </label>
        <div className="grid sm:grid-cols-2 gap-4">
          <label className="block">
            <span className="text-[10px] uppercase tracking-wider text-white/50 font-bold">New password</span>
            <input data-testid="password-new-input" type="password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-tv-accent" />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-wider text-white/50 font-bold">Confirm new password</span>
            <input data-testid="password-confirm-input" type="password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-tv-accent" />
          </label>
        </div>
        <div className="flex justify-end">
          <button type="submit" disabled={pwBusy} data-testid="password-change-btn" className="bg-white/10 hover:bg-white/20 text-white font-bold px-5 py-2.5 rounded-full flex items-center gap-2 disabled:opacity-60">
            {pwBusy ? "Updating…" : <><Check className="w-4 h-4" /> Update password</>}
          </button>
        </div>
      </form>
    </div>
  );
}
